<template>
    <div class="coach-calendar-availability">
        <div class="calendars-wrapper">
            <input v-model="selectedDate" name="date" style="display: none" type="date" />
            <!-- Rules: [no weekday, valid date format (YYYY-MM-DD), no null, min date tomorrow, max date 999 days from now] -->
        </div>

        <div class="page-header header-fixed">
            <h1>{{ __('[headline] Increase availability') }} - {{ coach.user.fullname }}</h1>

            <div class="btn-group">
                <VButton :label="__('Cancel')" :href="urls.redirect" variant="secondary" size="sm" />
                <VButton :label="__('Confirm changes')" size="sm" type="submit" @onClick="submit" :disabled="numberOfSelectedSlots === 0" />
            </div>
        </div>

        <div v-if="isValidDate" class="availability-content">
            <div class="">
                <div class="availability-checkbox">
                    <h2>{{ __('New date') }}</h2>
                </div>

                <div class="calendar availability-extra">
                    <Calendar ref="Calendar" mode="availability_new" />
                </div>
            </div>

            <div class="availability-info">
                <div class="availability-remain">
                    <span>{{ __('Extra hours') }}</span>
                    <strong>+{{ numberFormat(extraHours, 1) }} {{ __('hr') }}</strong>
                </div>

                <div v-if="availabilityNew.isNotEmpty()" class="info-summary">
                    <strong class="summary-title">{{ __('Hour total') }}</strong>
                    <div class="summary-time">
                        <div v-for="{ date, times } in availabilityNewTimeCollection" :key="date">
                            <div v-for="([start_time, end_time], i) in times" :key="i" class="info-changes exchange">
                                <p>
                                    {{ moment(date).format('dddd D. M.') }} {{ start_time.format('LT') }} -
                                    {{ end_time.format('LT') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Calendar from '@/components/Calendar/Calendar.vue'
import SlotsParser from '@/core/SlotsParser'

export default {
    components: {
        Calendar
    },
    props: {
        coach: {
            type: Object,
            required: true
        },
        date: {
            type: String,
            required: false,
            default: null
        }
    },
    data() {
        return {
            selectedDate: this.date,
            submitted: false
        }
    },
    computed: {
        ...mapGetters({
            slotsGrid: 'general/slotsGrid'
        }),
        urls() {
            if (this.user.role === 'coach') {
                // for the coach editing his own availability
                return {
                    fetch: '/availability/calendar',
                    store: '/extra-availability',
                    redirect: '/dostupnost'
                }
            } else {
                // for the coach-admin editing the coach's availability
                return {
                    fetch: `/coaches/${this.coach.id}/availability/calendar`,
                    store: `/coaches/${this.coach.id}/extra-availability`,
                    redirect: `/terapeut/${this.coach.id}#tab-availability-change`
                }
            }
        },
        formattedSelectedDate() {
            return moment(this.selectedDate).format('YYYY-MM-DD')
        },
        // returns collection of newly selected slots
        availabilityNew() {
            return this.slotsGrid.get().where('type', 'availability_new')
        },
        // counts duration of newly selected slots
        extraHours() {
            return SlotsParser.use(this.availabilityNew).getDuration()
        },
        // counts number of newly selected slots
        numberOfSelectedSlots() {
            return SlotsParser.use(this.availabilityNew).countSlots()
        },
        availabilityNewTimeCollection() {
            return SlotsParser.use(this.availabilityNew).toTimeCollection()
        },
        isValidDate() {
            const date = this.selectedDate

            // date is considered invalid if the format is invalid or the date is not yet defined
            if (!date || !moment(date, 'YYYY-MM-DD').isValid()) {
                return false
            }

            // tomorrow is the minimum date
            return moment(date).isAfter(moment(), 'day')
        }
    },
    mounted() {
        this.fetchSlots()

        this.slotsGrid.setOption('show-dates', true)
        this.slotsGrid.setOption('tooltip-table', {
            opening_hours: lang.get('Time not available'),
            vacations: lang.get('Time not available'),
            holidays: lang.get('Choose a new time'),
            availability_new: lang.get('Remove the selected term'),
            default: lang.get('Choose a new time'),
            consultations: lang.get('Consultation')
        })

        // availability_new can't select slots containing opening_hours, consultations, vacations type
        this.slotsGrid.addToBlacklist('availability_new', 'opening_hours')
        this.slotsGrid.addToBlacklist('availability_new', 'consultations')
        this.slotsGrid.addToBlacklist('availability_new', 'vacations')
    },
    methods: {
        async fetchSlots() {
            const data = await axios
                .get(this.urls.fetch, {
                    params: {
                        date: this.prepareDateRange()
                    }
                })
                .thenData()

            this.loadSlotsGridWithData(data)
        },
        loadSlotsGridWithData(data) {
            const openingHours = data.map((item) => {
                item.type = item.type !== 'consultations' && item.type !== 'vacations' && item.type !== 'holidays' ? 'opening_hours' : item.type
                return item
            })

            this.slotsGrid.append(openingHours)
        },
        // prepares range of dates is used for getting data for the calendar
        prepareDateRange() {
            const startDate = moment(this.selectedDate)
            const endDate = moment(this.selectedDate).add(1, 'month')

            return [startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD')]
        },
        async submit() {
            const numberOfSelectedSlots = this.numberOfSelectedSlots

            if (numberOfSelectedSlots === 0) {
                return alert('Zero slots were selected')
            }

            if (this.submitted) {
                return
            }

            this.submitted = true

            await axios.post(this.urls.store, {
                data: this.slotsGrid.convertForBackend().toJson()
            })
                // for testing
                // .then(() => {
                //     window.location.href = this.urls.redirect
                // })
                .catch((e) => {
                    // alert('Error occurred') // for testing
                    console.log(e.response)
                })

            window.location.href = this.urls.redirect
        }
    }
}
</script>
